import React, {useCallback, useEffect, useState} from "react";

import './_editDevice.scss'
import {Link, useNavigate, useParams} from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ButtonSet,
  Checkbox,
  CheckboxGroup,
  Column,
  Form,
  Grid,
  Loading,
  TextInput,
  Tile,
  FormGroup,
  InlineNotification
} from "@carbon/react";
import {useRecoilValue} from "recoil";
import {grpcDeviceServiceSelector} from "../../../state/grpc";
import {ADD, useToastContext} from "../../../components/Toast";
import DeviceAlarmsTable from "../../../components/DeviceAlarmsTable";

const EditDevice = () => {
  const {systemCode, deviceID} = useParams();
  const createCmd = deviceID === undefined
  const [deviceCode, setDeviceCode] = useState("")
  const [suspended, setSuspended] = useState(false)
  const [h24Disabled, setH24Disabled] = useState(false)
  const deviceService = useRecoilValue(grpcDeviceServiceSelector)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [device, setDevice] = useState({})
  const navigate = useNavigate();
  const {toastDispatch} = useToastContext();

  const navigateToDevicesList = useCallback((systemCode) => {
    navigate('/customers/' + systemCode + '/devices')
  }, [navigate])

  useEffect(() => {
    if (createCmd) {
      setLoading(false)
      return
    }
    const req = new proto.api.GetDeviceRequest()
    req.setId(deviceID)
    setLoading(true)
    deviceService.getDevice(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
        setError(true)
      } else {
        const device = resp.toObject().device
        setDeviceCode(device.code)
        setSuspended(device.suspended)
        setH24Disabled(device.h24Disabled)
        setDevice(device)
        setError(false)
      }
      setLoading(false)
    })
  }, [deviceID])

  if (loading) {
    return (
      <Loading withOverlay={true}/>
    )
  }

  if (error) {
    return (
      <span>ERROR!</span>
    )
  }

  const onSubmit = (ev) => {
    ev.stopPropagation()
    const req = new proto.api.SaveDeviceRequest()
    const deviceReq = new proto.api.Device()
    deviceReq.setCustomerCode(systemCode)
    deviceReq.setCode(deviceCode)
    deviceReq.setSuspended(suspended)
    deviceReq.setH24Disabled(h24Disabled)
    if (!createCmd) {
      deviceReq.setId(device.id)
      req.setDevice(deviceReq)
      deviceService.updateDevice(req, null, handleCustomerSave)
    } else {
      req.setDevice(deviceReq)
      deviceService.createDevice(req, null, handleCustomerSave)
    }
  }

  const handleCustomerSave = (err) => {
    if (err != null) {
      console.error(err)
      toastDispatch({
        type: ADD,
        payload: {
          title: "Operazione fallita",
          content: "Impossibile salvare lo strumento '" + deviceCode + "'",
          kind: "error",
          caption: err.message
        }
      })
    } else {
      toastDispatch({
        type: ADD,
        payload: {
          title: "Operazione avvenuta con successo",
          content: "Strumento '" + deviceCode + "' salvato con successo",
          kind: "success"
        }
      })
      navigateToDevicesList(systemCode)
    }
  }

  return (
    <Grid fullWidth className="customers-page">
      <Column lg={16} md={8} sm={4}>
        <Grid className="page-banner">
          <Column lg={16} md={4} sm={4}>
            <Breadcrumb noTrailingSlash aria-label="Page navigation">
              <BreadcrumbItem>
                <Link to='/'>Home</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <Link to='/customers'>Sistemi</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <Link to={'/customers/' + systemCode}>{systemCode}</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <Link to={'/customers/' + systemCode + '/devices'}>Strumenti</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                {device.code !== undefined ? device.code : "Nuovo"}
              </BreadcrumbItem>
            </Breadcrumb>
          </Column>
          <Column lg={16} md={4} sm={4}>
            <h1 className="page-heading">
              {device.code !== undefined ? "Configura strumento " + device.code : "Crea strumento"}
            </h1>
          </Column>
        </Grid>
        <Grid>
          <Column lg={16} md={8} sm={4} className="customers-edit-page__r1">
            <Tile>
              <FormGroup style={{maxWidth: '400px'}} legendText="Configurazione strumento">
                <Form style={{marginTop: '1rem'}} onSubmit={(ev) => onSubmit(ev)}>
                  <TextInput
                    id="device-code"
                    labelText="Codice strumento"
                    placeholder="Codice strumento"
                    required={true}
                    disabled={!createCmd}
                    style={{marginBottom: '1rem'}}
                    value={deviceCode}
                    type="text"
                    onChange={ev => setDeviceCode(ev.target.value)}
                  />
                  <CheckboxGroup legendText="Sospendi strumento">
                    <Checkbox
                      id="device-suspended"
                      labelText="Sospeso"
                      invalidText="Valore non valido"
                      warnText="Attenzione"
                      checked={suspended}
                      style={{marginBottom: '1rem'}}
                      onChange={(ev, {checked}) => {
                        setSuspended(checked)
                      }}
                    />
                  </CheckboxGroup>
                  <div style={{marginBottom: '1rem'}}/>
                  <CheckboxGroup legendText="Disabilita H24">
                    <Checkbox
                      id="device-h24-disabled"
                      labelText="H24 disabilitato"
                      invalidText="Valore non valido"
                      warnText="Attenzione"
                      checked={h24Disabled}
                      style={{marginBottom: '1rem'}}
                      onChange={(ev, {checked}) => {
                        setH24Disabled(checked)
                      }}
                    />
                  </CheckboxGroup>
                  <ButtonSet className="devices-edit-page__r1-form-btn">
                    <Button onClick={() => navigateToDevicesList(systemCode)} kind="secondary">
                      Annulla
                    </Button>
                    <Button disabled={saving} type="submit" kind="primary">
                      {saving ? "Salvataggio in corso.." : "Salva"}
                    </Button>
                  </ButtonSet>
                </Form>
              </FormGroup>
            </Tile>
            <Tile style={{marginTop: '40px'}}>
              <FormGroup legendText="Mappatura allarmi">
                {
                  createCmd &&
                  <InlineNotification
                    subtitle="Prima di poterne mappare gli allarmi, devi salvare lo strumento."
                    statusIconDescription="Attenzione"
                    kind="info"
                    lowContrast={true}
                    hideCloseButton={true}/>
                }
                {
                  !createCmd && <DeviceAlarmsTable device={device} />
                }
              </FormGroup>
            </Tile>
          </Column>
        </Grid>
      </Column>
    </Grid>
  )
}

export default EditDevice